import { call, put, select, takeLatest } from 'redux-saga/effects';
import { CLEANERS_ACTIONS, CLEANER_LOADERS } from './utils';
import { selectSearchData } from '../app/selectors';
import {
  selectCleanerProfile,
  selectCleaners,
  selectEditingCleaner,
  selectFavorites,
  selectNewCleaner,
} from './selectors';
import { CleanerProfile, Favorites, NewCleaner, Slot } from './model';
import { toast } from 'sonner';
import {
  findSlots,
  removeFavorite,
  resetEditingCleaner,
  setCleaners,
  setCleanerSlots,
  setCleanerProfile,
  setCurrentCleaner,
  setEditingCurrentCleaner,
  setFavorites,
  setHasMore,
  toggleLoader,
  getCleanerProfile as getCleanerProfileAction,
  changeCleanerVisibility,
  setCleanerVisibility,
} from './reducer';
import { sagaNavigator } from '@/utils/navigator';
import { APP_ROUTES } from '@/utils/appRoutes';
import { SearchForm } from '../app/model';
import { createCleaner } from '@/api/requests/endpoints/POST/createCleaner';
import { PayloadAction } from '@reduxjs/toolkit';
import { findCleanerById } from '@/api/requests/endpoints/GET/findCleanerById';
import { removeFromFavorites } from '@/api/requests/endpoints/POST/removeFromFavorites';
import { searchFavorites } from '@/api/requests/endpoints/GET/searchFavorites';
import { addFavorite } from '@/api/requests/endpoints/POST/addFavorite';
import { searchCleaners } from '@/api/requests/endpoints/GET/searchCleaners';
import { getAvailableSlots } from '@/api/requests/endpoints/GET/getAvailableSlots';
import dayjs from 'dayjs';
import { getCleanerProfile } from '@/api/requests/endpoints/GET/getCleanerProfile';
import { updateCleaner } from '@/api/requests/endpoints/POST/updateCleaner';
import { changeSettings } from '../app/reducer';
import { UpdateCleanerDTO } from '@/api/requests/DTOS';
import { updateCleanerVisibility } from '@/api/requests/endpoints/POST/updateCleanerVisibility';

function* searchCleanersSaga({ payload }: PayloadAction<number>) {
  const LOADER =
    payload > 1 ? CLEANER_LOADERS.LOAD_MORE : CLEANER_LOADERS.SEARCH_CLEANERS;
  try {
    yield put(toggleLoader({ key: LOADER, value: true }));
    const searchForm: SearchForm = yield select(selectSearchData);
    const currentCleaners = yield select(selectCleaners);

    const baseDate = dayjs.unix(searchForm.date.day);
    const [slotHour, slotMinute] = searchForm.date.slot.split(':').map(Number);
    const start_time = baseDate
      .hour(slotHour)
      .minute(slotMinute)
      .second(0)
      .millisecond(0);
    const { coords } = searchForm.location;

    const searchData = {
      start_time: start_time.format('YYYY-MM-DD HH:mm:ss'),
      page: payload,
      lat: Number(coords.lat),
      lng: Number(coords.lng),
    };

    const response = yield searchCleaners(searchData);

    if (response) {
      const newCleaners = (response.cleaners ?? []).map(
        ({ lat, lng, id, user_id: _, ...rest }) => ({
          ...rest,
          coords: { lat, lng },
          id,
        }),
      );
      const allCleaners =
        payload > 1 ? [...currentCleaners, ...newCleaners] : newCleaners;
      yield put(setCleaners(allCleaners));
      yield put(setHasMore(response.hasMore));
    }
  } catch (e) {
    toast.error('Si è verificato un errore', {
      style: {
        backgroundColor: '#ff0033',
        color: '#FFF',
      },
      position: 'top-right',
      duration: 10000,
      description: 'Non è stato possibile effettuare la ricerca',
    });
  } finally {
    yield put(toggleLoader({ key: LOADER, value: false }));
  }
}

function* createCleanersSaga() {
  try {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.CREATE_CLEANER, value: true }),
    );
    const newCleaner: Omit<NewCleaner, 'slots'> & {
      slots: Record<
        string,
        {
          start_time: string;
          end_time: string;
        }[]
      >;
    } = yield select(selectNewCleaner);
    const { location, hourlyPrice, slots, bio } = newCleaner;
    console.log('SLOTS', slots);
    const payload = {
      lat: Number(location.coords.lat),
      lng: Number(location.coords.lng),
      place: location.displayName,
      price: hourlyPrice,
      slots: Object.entries(slots).reduce(
        (acc, [key, items]) =>
          items.length
            ? {
                ...acc,
                [Number(key)]: items.map(({ start_time, end_time }) => ({
                  dayOfWeek: Number(key),
                  startTime: Number(start_time),
                  endTime: Number(end_time),
                })),
              }
            : acc,
        {},
      ),
      bio,
    };
    const res = yield createCleaner(payload);
    if (res.error) {
      throw new Error('Somenthing went wrong');
    }
    toast.success('Operazione completata con successo', {
      position: 'top-right',
      duration: 10000,
      description: 'Ora puoi iniziare a collaborare con noi',
    });
    yield put(changeSettings({ key: 'mode', value: 'cleaner' }));
    yield sagaNavigator(APP_ROUTES.CLEANER_DASHBOARD);
  } catch (e) {
    toast.error('Si è verificato un errore', {
      style: {
        backgroundColor: '#ff0033',
        color: '#FFF',
      },
      position: 'top-right',
      duration: 10000,
      description: "Non è stato possibile completare l'operazione",
    });
  } finally {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.CREATE_CLEANER, value: false }),
    );
  }
}

function* getCleanerDetailSaga({ payload }: PayloadAction<string>) {
  try {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.GET_CLEANER_DETAIL, value: true }),
    );
    const cleaner = yield findCleanerById(payload);
    if (!cleaner) {
      throw new Error('Something went wrong');
    }

    yield put(setCurrentCleaner(cleaner));
  } catch (e) {
    toast.error('Si è verificato un errore', {
      style: {
        backgroundColor: '#ff0033',
        color: '#FFF',
      },
      position: 'top-right',
      duration: 10000,
      description: 'Non è stato possibile effettuare la ricerca',
    });
  } finally {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.GET_CLEANER_DETAIL, value: false }),
    );
  }
}

function* getCleanersSlotsSaga({ payload }: ReturnType<typeof findSlots>) {
  try {
    yield put(toggleLoader({ key: CLEANER_LOADERS.FIND_SLOTS, value: true }));
    const { date: searchDate }: SearchForm = yield select(selectSearchData);
    const { cleanerId, date } = payload;
    const currentDate = date ?? searchDate.day;
    const dateAtNoon = dayjs
      .unix(currentDate)
      .hour(12)
      .minute(0)
      .second(0)
      .millisecond(0);
    const formatted = dateAtNoon.format();
    const slots = yield getAvailableSlots({
      id: cleanerId,
      start_time: formatted,
    });
    const mappedSlots = {
      [dayjs.unix(currentDate).day()]: slots ?? [],
    };
    yield put(setCleanerSlots(mappedSlots));
  } catch (e) {
    toast.error(
      'Si è verificato un errore nel recupero degli slot disponibili',
      {
        style: {
          backgroundColor: '#ff0033',
          color: '#FFF',
        },
        position: 'top-right',
        duration: 10000,
        description: 'Non è stato possibile effettuare la ricerca',
      },
    );
  } finally {
    yield put(toggleLoader({ key: CLEANER_LOADERS.FIND_SLOTS, value: false }));
  }
}

function* fetchFavoritesSaga() {
  try {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.SEARCH_FAVORITES, value: true }),
    );
    const data = yield searchFavorites();
    yield put(setFavorites(data ?? []));
  } catch (e) {
    console.log(e);
  } finally {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.SEARCH_FAVORITES, value: false }),
    );
  }
}

function* removeFavoriteSaga({ payload }: ReturnType<typeof removeFavorite>) {
  try {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.TOGGLE_FAVORITE, value: true }),
    );
    const allFavorites: Favorites = yield select(selectFavorites);
    yield removeFromFavorites(payload);
    yield put(
      setFavorites(
        allFavorites.filter(({ cleanerId }) => cleanerId !== payload),
      ),
    );
  } catch (e) {
    toast.error(e);
  } finally {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.TOGGLE_FAVORITE, value: false }),
    );
  }
}

function* addFavoriteSaga({ payload }: PayloadAction<string>) {
  try {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.TOGGLE_FAVORITE, value: true }),
    );
    yield addFavorite(payload);
    yield call(fetchFavoritesSaga);
  } catch (e) {
    toast.error(e);
  } finally {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.TOGGLE_FAVORITE, value: false }),
    );
  }
}

function* getCleanerProfileSaga() {
  try {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.GET_CLEANER_PROFILE, value: true }),
    );

    const response = yield getCleanerProfile();
    yield put(setCleanerProfile(response));
  } catch (e) {
    toast.error('Si è verificato un errore nel recupero dei dati', {
      style: {
        backgroundColor: '#ff0033',
        color: '#FFF',
      },
      position: 'top-right',
      duration: 10000,
    });
    yield sagaNavigator(APP_ROUTES.PROFILE);
  } finally {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.GET_CLEANER_PROFILE, value: false }),
    );
  }
}

function* setEditingCleanerSaga() {
  const { place, price, bio, slots, lat, lng }: CleanerProfile =
    yield select(selectCleanerProfile);
  yield put(
    setEditingCurrentCleaner({
      price,
      bio,
      location: {
        place,
        coords: {
          lat,
          lng,
        },
      },
      slots,
    }),
  );
}

function* updateCleanerDataSaga() {
  try {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.UPDATE_CLEANER_DATA, value: true }),
    );
    const { place, lat, lng, slots, id, price, bio } =
      yield select(selectCleanerProfile);

    const originalData = {
      location: { place, coords: { lat, lng } },
      slots,
      id,
      price,
      bio,
    };
    const editingData = yield select(selectEditingCleaner);
    const slotsOperations = Object.entries(originalData.slots).reduce(
      (acc, [key, value]: [string, Slot[]]) => {
        value.forEach(({ _id, startTime, endTime }) => {
          const newSlot = editingData.slots[key]?.find(
            ({ _id: slotId }) => slotId === _id,
          );

          if (!newSlot) {
            acc.toRemove.push({ _id, dayOfWeek: key });
          }
          if (
            newSlot &&
            (newSlot.startTime !== startTime || newSlot.endTime !== endTime)
          ) {
            acc.toUpdate.push({
              _id,
              startTime: newSlot.startTime,
              endTime: newSlot.endTime,
              dayOfWeek: key,
            });
          }
        });
        return acc;
      },
      {
        toRemove: [],
        toUpdate: [],
        toInsert: [],
      },
    );

    slotsOperations.toInsert = Object.entries(editingData.slots).reduce(
      (acc, [key, value]: [string, (Slot & { _id?: string })[]]) => {
        value.forEach(({ _id, startTime, endTime }) => {
          if (!_id) {
            acc.push({
              startTime,
              endTime,
              dayOfWeek: key,
            });
          }
        });
        return acc;
      },
      [],
    );

    const dataDiff = {
      slotsOperations,
      bio: editingData.bio !== originalData.bio ? editingData.bio : undefined,
      price:
        editingData.price !== originalData.price
          ? editingData.price
          : undefined,
      place:
        editingData.location.place !== originalData.location.place
          ? editingData.location.place
          : undefined,
      location:
        editingData.location.place !== originalData.location.place
          ? {
              type: 'Point',
              coordinates: [lng, lat],
            }
          : undefined,
    };

    const payload = Object.entries(dataDiff).reduce(
      (acc, [key, value]) => (value ? { ...acc, [key]: value } : acc),
      {},
    );
    yield updateCleaner(payload as UpdateCleanerDTO);
    yield put(getCleanerProfileAction());
    yield put(resetEditingCleaner());
  } catch (e) {
    toast.error('Si è verificato un errore nella modifica dei dati', {
      style: {
        backgroundColor: '#ff0033',
        color: '#FFF',
      },
      position: 'top-right',
      duration: 10000,
    });
  } finally {
    yield put(
      toggleLoader({ key: CLEANER_LOADERS.UPDATE_CLEANER_DATA, value: false }),
    );
  }
}

function* changeCleanerVisibilitySaga({
  payload,
}: ReturnType<typeof changeCleanerVisibility>) {
  try {
    yield put(
      toggleLoader({
        key: CLEANER_LOADERS.CHANGE_CLEANER_VISIBILITY,
        value: true,
      }),
    );
    const res = yield updateCleanerVisibility(!payload);
    if (!res) {
      throw new Error('Something went wrong');
    }
    yield put(setCleanerVisibility(res.disabled));
  } catch (e) {
    toast.error('Si è verificato un errore durante la modifica', {
      style: {
        backgroundColor: '#ff0033',
        color: '#FFF',
      },
      position: 'top-right',
      duration: 10000,
    });
  } finally {
    yield put(
      toggleLoader({
        key: CLEANER_LOADERS.CHANGE_CLEANER_VISIBILITY,
        value: false,
      }),
    );
  }
}

export function* cleanersSaga() {
  yield takeLatest(CLEANERS_ACTIONS.SEARCH_CLEANERS, searchCleanersSaga);
  yield takeLatest(CLEANERS_ACTIONS.CREATE_CLEANER, createCleanersSaga);
  yield takeLatest(CLEANERS_ACTIONS.GET_CLEANER_DETAIL, getCleanerDetailSaga);
  yield takeLatest(CLEANERS_ACTIONS.FETCH_FAVORITES, fetchFavoritesSaga);
  yield takeLatest(CLEANERS_ACTIONS.ADD_FAVORITE, addFavoriteSaga);
  yield takeLatest(CLEANERS_ACTIONS.REMOVE_FAVORITE, removeFavoriteSaga);
  yield takeLatest(CLEANERS_ACTIONS.FIND_SLOTS, getCleanersSlotsSaga);
  yield takeLatest(CLEANERS_ACTIONS.GET_CLEANER_PROFILE, getCleanerProfileSaga);
  yield takeLatest(CLEANERS_ACTIONS.SET_EDITING_CLEANER, setEditingCleanerSaga);
  yield takeLatest(CLEANERS_ACTIONS.UPDATE_CLEANER_DATA, updateCleanerDataSaga);
  yield takeLatest(
    CLEANERS_ACTIONS.CHANGE_CLEANER_VISIBILITY,
    changeCleanerVisibilitySaga,
  );
}
